import { useContentDeliveryServiceV2 } from "@rw1/api-services";
import { ContentDelivery_V2 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { useFetchAll } from "./useFetchAll";

export const ALL_CONTENT_METADATA_QUERY_KEY = "allContentMetadata";

interface UseAllContentMetadataOptions {
  pageSize?: number;
  contentMetadataNames?: string[];
  search?: string;
  enabled?: boolean;
  includeBinaries?: boolean;
}

interface UseAllContentMetadataResult {
  contentMetadata: ContentDelivery_V2.ContentMetadata[];
  contentMetadataMap: Map<string, ContentDelivery_V2.ContentMetadata>;
  isLoading: boolean;
  error: Error | null;
}

export const useAllContentMetadata = (
  options: UseAllContentMetadataOptions = {},
): UseAllContentMetadataResult => {
  const {
    pageSize = 1000,
    contentMetadataNames,
    search,
    enabled = true,
    includeBinaries = false,
  } = options;

  const contentDeliveryClient = useContentDeliveryServiceV2();

  const queryKey = [
    ALL_CONTENT_METADATA_QUERY_KEY,
    contentMetadataNames ?? "all",
    search,
    includeBinaries,
  ];

  const { data, isLoading, error } =
    useFetchAll<ContentDelivery_V2.ContentMetadata>(
      queryKey,
      (page, size) =>
        contentDeliveryClient.getContentMetadata(
          page,
          size,
          contentMetadataNames,
          search,
          includeBinaries,
        ),
      {
        enabled:
          enabled && (!contentMetadataNames || contentMetadataNames.length > 0),
        pageSize,
      },
    );

  const contentMetadataMap = useMemo(() => {
    const map = new Map<string, ContentDelivery_V2.ContentMetadata>();
    if (data) {
      for (const metadata of data) {
        map.set(metadata.contentMetadataId, metadata);
      }
    }

    return map;
  }, [data]);

  return {
    contentMetadata: data ?? [],
    contentMetadataMap,
    isLoading,
    error,
  };
};
